// "Maps & routing" on the Ops overview (map architecture, 2026-10-07): is the basemap served from our
// own file, are the label fonts ours, and is road routing answering. A routing outage used to be
// silent (every tracking line quietly went straight); this makes it visible at a glance.
//
// /ops/maps, Map health (native maps, docs/NATIVE-MAPS-CONTRACT.md §3-4): the same card, then how much
// of the city our own traffic covers (and the speeds file for routing), the landmarks riders added
// that wait for a look, and the map gaps: roads riders use that the map lacks, each one a link
// into the OpenStreetMap editor. Fixing OSM fixes our next basemap and router build.
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { OpsLandmark, PlaceCategory } from '@popcart/core/v1';
import { api, errorMessage, sessionToken } from '../../api';
import { ErrorState, Loading, Segmented, useNow, useTitle } from '../../ui/bits';
import { Icon, type IconName } from '../../ui/Icon';
import { Fig, PageHead, Ring } from '../../ui/viz';
import { useToast } from '../../ui/toast';
import { count, percent } from '../../lib/format.js';
import { manilaStamp } from '../reports/period.js';
import { DialogActions, Modal, StaleNote, TextField } from '../payments/kit';
import { useMapGaps, useMapLandmarks, useTrafficCoverage, useUpdateLandmark } from '../queries';
import { ItemMenu } from '../zones/Menu';
import { GapsMap } from './GapsMap';
import {
  CATEGORY_LABELS,
  PLACE_CATEGORIES,
  confirmationsLabel,
  fetchSegmentSpeedsCsv,
  hourOfWeek,
  hourOfWeekLabel,
  landmarkEditPatch,
  landmarkStatusPatch,
  latLngLabel,
  osmEditUrl,
  osmViewUrl,
  speedsFileName,
  type LandmarkPatch,
  type LandmarkStatus,
} from './lib.js';
import './maphealth.css';
import { Select } from '../../ui/controls';

type Tone = 'ok' | 'warn' | 'bad';

/** One service: an icon disc carrying a status dot, the name and a short state, the figure right. */
function Service({ icon, name, tone, state, value }: { icon: IconName; name: string; tone: Tone; state: string; value: ReactNode }) {
  return (
    <li className={`mh-svc-row ${tone}`}>
      <span className="ic" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <span className="nm">
        <b>{name}</b>
        <span className="dv-sub">{state}</span>
      </span>
      {value}
    </li>
  );
}

export function MapHealthCard({ linked = false, className = '' }: { linked?: boolean; className?: string }) {
  const health = useQuery({ queryKey: ['ops', 'map-health'], queryFn: () => api.mapHealth(), refetchInterval: 60_000 });
  const h = health.data;
  const basemap = h?.basemap;
  const ownTiles = !!basemap && basemap.source === 'self-hosted';
  const routingOk = h?.routing.status === 'ok';
  const allGood = !!h && routingOk && ownTiles && h.fonts === 'self-hosted';
  return (
    <section className={`card dv-card ${className}`.trim()} aria-labelledby="maps-h">
      <div className="dv-card-h">
        <h2 id="maps-h">Maps &amp; routing</h2>
        {h ? <span className={allGood ? 'dv-chip good' : routingOk ? 'dv-chip' : 'dv-chip bad'}>{allGood ? 'All ours' : routingOk ? 'Fallback' : 'Down'}</span> : null}
        <span className="grow" />
        {linked ? (
          <Link to="/ops/maps" className="dv-link">
            Map health <Icon name="chevronRight" />
          </Link>
        ) : null}
      </div>
      {health.isError ? (
        <div className="dv-quiet">Can't reach the map services · retrying</div>
      ) : !h ? (
        <div className="dv-quiet">Checking…</div>
      ) : (
        <ul className="mh-svc">
          <Service
            icon="swap"
            name="Road routing"
            tone={routingOk ? 'ok' : 'bad'}
            state={routingOk ? 'Answering' : 'Lines go straight'}
            value={routingOk ? <Fig text={`${h.routing.latencyMs} ms`} size="md" /> : <span className="dv-chip bad">Down</span>}
          />
          <Service
            icon="globe"
            name="Map tiles"
            tone={ownTiles ? 'ok' : 'warn'}
            state={ownTiles && basemap ? `Ours · built ${new Date(basemap.builtAt).toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })}` : 'From OpenFreeMap'}
            value={ownTiles && basemap ? <Fig text={`${basemap.sizeMb} MB`} size="md" /> : <span className="dv-chip">Borrowed</span>}
          />
          <Service
            icon="list"
            name="Map labels"
            tone={h.fonts === 'self-hosted' ? 'ok' : 'warn'}
            state={h.fonts === 'self-hosted' ? 'Self-hosted fonts' : 'From OpenFreeMap'}
            value={<span className={h.fonts === 'self-hosted' ? 'dv-chip good' : 'dv-chip'}>{h.fonts === 'self-hosted' ? 'Ours' : 'Borrowed'}</span>}
          />
        </ul>
      )}
    </section>
  );
}

/** /ops/maps: routing and tiles, traffic coverage, landmarks to review, map gaps. */
export function OpsMapHealthPage() {
  useTitle('Map health');
  return (
    <>
      <PageHead title="Map health" meta="Our map · our traffic · our places" />
      <div className="dv-grid">
        <MapHealthCard className="dv-span-5 dv-rise mh-stretch" />
        <TrafficCoverageCard />
        <LandmarksCard />
        <GapsCard />
      </div>
    </>
  );
}

// ── traffic coverage ─────────────────────────────────────────────────────────────────────────
/** Save a Blob as a file via a temporary object URL. */
function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function TrafficCoverageCard() {
  const toast = useToast();
  const q = useTrafficCoverage();
  const hour = hourOfWeek(useNow(60_000));
  const download = useMutation({
    mutationFn: (h: number) => fetchSegmentSpeedsCsv(h, { token: sessionToken.get() }),
    onSuccess: (blob, h) => {
      saveBlob(blob, speedsFileName(h));
      toast(`Saved ${speedsFileName(h)}.`);
    },
    onError: (err) => toast(errorMessage(err, "Couldn't download the speeds. Try again."), 'error'),
  });
  const cov = q.data;
  const livePct = cov && cov.segmentsTypical > 0 ? Math.min(100, Math.round((cov.segmentsLive / cov.segmentsTypical) * 100)) : null;
  return (
    <section className="card dv-card dv-span-7 dv-rise mh-stretch" aria-labelledby="mh-traffic-h" style={{ ['--i' as string]: 1 }}>
      <div className="dv-card-h">
        <h2 id="mh-traffic-h">Traffic coverage</h2>
        <span className="grow" />
        <button
          type="button"
          className="btn btn-secondary mh-csv"
          disabled={download.isPending}
          aria-busy={download.isPending || undefined}
          aria-label="Download speeds for routing (CSV)"
          title={`Speeds for ${hourOfWeekLabel(hour)} Manila`}
          onClick={() => download.mutate(hour)}
        >
          <Icon name="arrow" />
          {download.isPending ? 'Preparing…' : 'Speeds CSV'}
        </button>
      </div>
      {q.isError && !cov ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !cov ? (
        <Loading label="Loading traffic coverage" />
      ) : (
        <>
          <StaleNote show={q.isError} what="traffic coverage" />
          <div className="mh-cov">
            <div className="mh-cov-ring" aria-label={`Live now: ${percent(livePct)} of the usual roads this hour`}>
              <Ring pct={livePct} tone={livePct === null ? 'warn' : livePct >= 50 ? 'good' : livePct >= 10 ? 'warn' : 'bad'}>
                <Fig text={livePct === null ? '—' : `${livePct}%`} size="sm" />
              </Ring>
              <span className="dv-label">Live now</span>
            </div>
            <div className="st-figs mh-cov-figs">
              <div>
                <span className="dv-label">Live roads</span>
                <Fig text={count(cov.segmentsLive)} size="md" />
              </div>
              <div>
                <span className="dv-label">Usual this hour</span>
                <Fig text={count(cov.segmentsTypical)} size="md" />
              </div>
              <div>
                <span className="dv-label">Samples · 7 days</span>
                <Fig text={count(cov.samples7d)} size="md" />
              </div>
              <div>
                <span className="dv-label">Riders · 7 days</span>
                <Fig text={count(cov.riders7d)} size="md" />
              </div>
            </div>
          </div>
          <span className="dv-sub">
            <Icon name="clock" /> Last sample: {cov.lastSampleAt ? `${manilaStamp(cov.lastSampleAt)} Manila` : 'none yet'}
          </span>
        </>
      )}
      <details className="st-more">
        <summary>How traffic and the speeds file work</summary>
        <p>
          Traffic on our maps comes only from PopCart riders on a job, never from an outside service. A road shows live traffic once at least 2 riders have driven it in the last 20 minutes;
          otherwise it shows its usual speed for this hour. No single rider's trip is ever shown.
        </p>
        <p>
          The CSV is for {hourOfWeekLabel(hour)} Manila · hour {hour} of the week (Sunday 00:00 = 0). Routing doesn't change by itself: apply the file by hand on valmoria, following <code>ops/osrm/apply-speeds.md</code>.
        </p>
      </details>
    </section>
  );
}

// ── landmarks ────────────────────────────────────────────────────────────────────────────────
const CATEGORY_ICON: Partial<Record<string, IconName>> = { food: 'flame', cafe: 'flame', shop: 'bag', grocery: 'bag', health: 'plus', school: 'list', worship: 'star', transit: 'swap', bank: 'cash', fuel: 'bolt', gov: 'home', park: 'globe', landmark: 'pin' };

function LandmarksCard() {
  const [status, setStatus] = useState<LandmarkStatus>('pending');
  const pending = useMapLandmarks('pending');
  const other = useMapLandmarks(status === 'pending' ? 'verified' : status, status !== 'pending');
  const q = status === 'pending' ? pending : other;
  const update = useUpdateLandmark();
  const [editing, setEditing] = useState<OpsLandmark | null>(null);
  const waiting = pending.data?.length ?? null;
  const list = q.data ?? [];

  const act = (l: OpsLandmark, action: 'approve' | 'hide') => update.mutate({ landmark: l, patch: landmarkStatusPatch(action) });

  return (
    <section className="card dv-card dv-span-12 dv-rise" aria-labelledby="mh-landmarks-h" style={{ ['--i' as string]: 2 }}>
      <div className="dv-card-h mh-wrap-h">
        <h2 id="mh-landmarks-h">Landmarks to review</h2>
        {waiting ? <span className="dv-chip">{waiting}</span> : null}
        <span className="grow" />
        <Segmented<LandmarkStatus>
          label="Show"
          value={status}
          options={[
            { value: 'pending', label: waiting ? `Pending · ${waiting}` : 'Pending' },
            { value: 'verified', label: 'On the map' },
            { value: 'hidden', label: 'Hidden' },
          ]}
          onChange={setStatus}
        />
      </div>
      {q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Loading label="Loading landmarks" rows={2} />
      ) : list.length === 0 ? (
        <div className="dv-quiet">
          <Icon name="check" />
          {status === 'pending' ? 'Nothing to review' : status === 'verified' ? 'No landmarks on the map yet' : 'Nothing hidden'}
        </div>
      ) : (
        <>
          <StaleNote show={q.isError} what="the landmarks" />
          <ul className="mh-lms">
            {list.map((l, j) => {
              const busy = update.isPending && update.variables?.landmark.id === l.id;
              const on = Math.min(2, l.riders);
              return (
                <li key={l.id} className={`mh-lm ${l.status}`} style={{ ['--i' as string]: j }}>
                  <span className="mh-lm-ic" aria-hidden="true">
                    <Icon name={CATEGORY_ICON[l.category] ?? 'pin'} />
                  </span>
                  <span className="mh-lm-nm">
                    <b>{l.name}</b>
                    <span className="dv-sub">
                      {CATEGORY_LABELS[l.category] ?? l.category} · {manilaStamp(l.createdAt)}
                    </span>
                  </span>
                  <span className="mh-lm-conf" title="Two riders put a landmark on the map">
                    <span className="mh-pips" aria-hidden="true">
                      <i className={on >= 1 ? 'on' : ''} />
                      <i className={on >= 2 ? 'on' : ''} />
                    </span>
                    <span className="dv-sub">{confirmationsLabel(l)}</span>
                  </span>
                  <a className="mh-lm-where" href={osmViewUrl(l.location)} target="_blank" rel="noopener noreferrer" aria-label={`See ${l.name} on the map (opens OpenStreetMap)`} title={latLngLabel(l.location)}>
                    <Icon name="pin" />
                  </a>
                  <span className="mh-lm-act">
                    {l.status !== 'verified' ? (
                      <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => act(l, 'approve')} aria-label={l.status === 'hidden' ? `Show ${l.name} again` : `Approve ${l.name}`}>
                        <Icon name="check" />
                        {l.status === 'hidden' ? 'Show again' : 'Approve'}
                      </button>
                    ) : null}
                  </span>
                  <ItemMenu
                    label={`More for ${l.name}`}
                    items={[
                      { label: 'Edit', ariaLabel: `Edit ${l.name}`, icon: 'gear', disabled: busy, onSelect: () => setEditing(l) },
                      ...(l.status !== 'hidden' ? [{ label: 'Hide', ariaLabel: `Hide ${l.name}`, icon: 'bellOff' as IconName, tone: 'danger' as const, disabled: busy, onSelect: () => act(l, 'hide') }] : []),
                    ]}
                  />
                </li>
              );
            })}
          </ul>
        </>
      )}
      <details className="st-more">
        <summary>How landmarks get on the map</summary>
        <p>Riders add the landmarks people give directions by. Two riders naming the same place puts it on every PopCart map; you can approve one sooner, fix its name or category, or hide it.</p>
      </details>
      <LandmarkDialog
        landmark={editing}
        onClose={() => setEditing(null)}
        onSave={(l, patch) => {
          update.mutate({ landmark: l, patch });
          setEditing(null);
        }}
      />
    </section>
  );
}

function LandmarkDialog({ landmark, onClose, onSave }: { landmark: OpsLandmark | null; onClose(): void; onSave(l: OpsLandmark, patch: LandmarkPatch): void }) {
  return (
    <Modal open={!!landmark} labelledBy="mh-edit-h" onClose={onClose}>
      {landmark ? <LandmarkForm key={landmark.id} landmark={landmark} onClose={onClose} onSave={onSave} /> : null}
    </Modal>
  );
}

function LandmarkForm({ landmark, onClose, onSave }: { landmark: OpsLandmark; onClose(): void; onSave(l: OpsLandmark, patch: LandmarkPatch): void }) {
  const [name, setName] = useState(landmark.name);
  const [category, setCategory] = useState<string>(landmark.category);
  const [err, setErr] = useState<{ field: 'name' | 'category'; error: string } | null>(null);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const r = landmarkEditPatch(landmark, { name, category });
    if (!r.ok) {
      setErr({ field: r.field, error: r.error });
      return;
    }
    if (r.value) onSave(landmark, r.value);
    else onClose();
  };
  return (
    <form className="stack s24" onSubmit={submit} noValidate>
      <h2 id="mh-edit-h" className="t-headline">
        Edit landmark
      </h2>
      <TextField id="mh-edit-name" label="Name" value={name} onChange={setName} maxLength={60} error={err?.field === 'name' ? err.error : undefined} help="The name people use for it, like “7-Eleven Purok 3”." />
      <div className="field">
        <label htmlFor="mh-edit-category">Category</label>
        <Select id="mh-edit-category" className="input" value={category} onChange={(e) => setCategory(e.target.value)} aria-invalid={err?.field === 'category' || undefined}>
          {PLACE_CATEGORIES.map((c: PlaceCategory) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </Select>
      </div>
      <DialogActions busy={false} confirm="Save" busyLabel="Saving…" onCancel={onClose} />
    </form>
  );
}

/// ── map gaps ─────────────────────────────────────────────────────────────────────────────────
function GapsCard() {
  const q = useMapGaps();
  const [selected, setSelected] = useState<string | null>(null);
  const gaps = q.data ?? [];
  const top = Math.max(1, ...gaps.map((g) => g.deliveries));
  return (
    <section className="card dv-card dv-span-12 dv-rise" aria-labelledby="mh-gaps-h" style={{ ['--i' as string]: 3 }}>
      <div className="dv-card-h">
        <h2 id="mh-gaps-h">Map gaps</h2>
        {q.data ? <span className={gaps.length ? 'dv-chip bad' : 'dv-chip good'}>{gaps.length === 1 ? '1 place' : `${gaps.length} places`}</span> : null}
      </div>
      {q.isError && !q.data ? (
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Loading label="Loading map gaps" rows={2} />
      ) : gaps.length === 0 ? (
        <div className="dv-quiet">
          <Icon name="check" />
          No map gaps
        </div>
      ) : (
        <>
          <StaleNote show={q.isError} what="the map gaps" />
          <div className="mh-grid">
            <GapsMap gaps={gaps} selectedId={selected} onSelect={setSelected} />
            <div className="table-wrap">
              <table className="mh-gaps">
                <thead>
                  <tr>
                    <th scope="col">Where</th>
                    <th scope="col" className="num">
                      Deliveries
                    </th>
                    <th scope="col" className="num">
                      Fixes
                    </th>
                    <th scope="col">Last seen</th>
                    <th scope="col">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {gaps.map((g) => (
                    <tr key={g.id} className={g.id === selected ? 'mh-selected' : undefined} aria-current={g.id === selected || undefined}>
                      <td>
                        <button type="button" className="mh-where" onClick={() => setSelected(g.id)} aria-label={`Show the gap at ${latLngLabel(g.center)} on the map`}>
                          <Icon name="pin" />
                          <span className="t-mono">{latLngLabel(g.center)}</span>
                        </button>
                      </td>
                      <td className="num">
                        <span className="mh-share">
                          <span className="dv-share" aria-hidden="true">
                            <i style={{ width: `${(g.deliveries / top) * 100}%` }} />
                          </span>
                          {count(g.deliveries)}
                        </span>
                      </td>
                      <td className="num">{count(g.fixes)}</td>
                      <td className="dv-sub">{manilaStamp(g.lastSeenAt)}</td>
                      <td className="num">
                        <a className="btn btn-secondary" href={osmEditUrl(g.center)} target="_blank" rel="noopener noreferrer">
                          Edit in OpenStreetMap
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
      <details className="st-more">
        <summary>What a map gap is</summary>
        <p>
          Roads riders use that our map doesn't have: good GPS fixes that routing couldn't put on any road, seen on 3 or more deliveries. Draw the missing road in OpenStreetMap and our next map and routing build picks it up.
        </p>
      </details>
    </section>
  );
}
